//
// Copyright 2020 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
//
// Copyright 2020 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React from "react";
import clsx from "clsx";
import {lighten, makeStyles} from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Toolbar from "@material-ui/core/Toolbar";
import CircularProgress from "@material-ui/core/CircularProgress";
import * as Utils from "eai-configurator-ui/components/Utils";
import Button from "@material-ui/core/Button";
import DownloadIcon from "@material-ui/icons/GetApp";
import DetailsIcon from "@material-ui/icons/Subject";
import ReplayIcon from "@material-ui/icons/Replay";
import ErrorIcon from "@material-ui/icons/Error";
import DeleteIcon from "@material-ui/icons/Delete";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import IconButton from "@material-ui/core/IconButton";
import TaskDetailsDialog from "src/components/loader/table/TaskDetailsDialog";
import Tooltip from "@material-ui/core/Tooltip";
import Moment from "moment";
import ErrorMessageDialog, {useErrorMessageDialog,} from "eai-configurator-ui/components/configuration/dialog/ErrorMessageDialog";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CheckBox from "@material-ui/core/CheckBox";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === "light"
      ? {
        color: theme.palette.secondary.main,
        backgroundColor: lighten(theme.palette.secondary.light, 0.85),
      }
      : {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.secondary.dark,
      },
  title: {
    flex: "1 1 100%",
  },
}));

const EnhancedTableToolbar = ({title}) => {
  const classes = useToolbarStyles();

  return (
    <Toolbar className={clsx(classes.root)}>
      <Typography className={classes.title} variant="h6" id="tableTitle">
        {title}
      </Typography>
    </Toolbar>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  addItem: {
    padding: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  table: {
    minWidth: 500,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

const createTimestampMessage = (timestampMillis) => {
  return Moment(new Date(timestampMillis)).fromNow();
};

export default function LoaderTasksTable({
                                           lastFileUploadTimestamp,
                                           title,
                                           taskTypes,
                                           enabledFeatures
                                         }) {
  const classes = useStyles();
  const [scheduledTimeoutId, setScheduledTimeoutId] = React.useState(null);
  const [tasks, setTasks] = React.useState([]);
  const [taskData, setTaskData] = React.useState({
    queuedManual: 0, startedManual: 0, completedManual: 0,
    queuedAutomated: 0, startedAutomated: 0, completedAutomated: 0,
    queuedTotal: 0, startedTotal: 0, completedTotal: 0,
    hasBothTypes: false
  });
  const [viewManualTasks, setViewManualTasks] = React.useState(true);
  const [viewAutomatedTasks, setViewAutomatedTasks] = React.useState(false);
  const [displayDetailsForTask, setDisplayDetailsForTask] = React.useState(
    null
  );
  const [
    lastTableRefreshTimestamp,
    setLastTableRefreshTimestamp,
  ] = React.useState(null);
  const componentIsMounted = React.useRef(true);
  const [errorMessage, setErrorMessage] = useErrorMessageDialog();

  React.useEffect(() => {
    if (lastTableRefreshTimestamp < lastFileUploadTimestamp) {
      populateTable(false);
    }
  }, [lastFileUploadTimestamp, lastTableRefreshTimestamp]);

  React.useEffect(() => {
    populateTable(true);
    return () => {
      componentIsMounted.current = false;
      if (scheduledTimeoutId) {
        clearTimeout(scheduledTimeoutId);
      }
    };
  }, []);

  const populateTable = (shouldRescheduleTimeout) => {
    setLastTableRefreshTimestamp(new Date().getTime());
    const types = taskTypes ? taskTypes : [];
    Utils.fetchData("loader/tasks?types=" + JSON.stringify(types))
      .then((data) => {
        setTasks(data.tasks);
        setTaskData(data);
        if (shouldRescheduleTimeout) {
          rescheduleTimeout();
        }
      })
      .catch(function (response) {
        Utils.getErrorMessage(response, "Failed to refresh tasks.").then(
          setErrorMessage
        );
        if (shouldRescheduleTimeout) {
          rescheduleTimeout();
        }
      });
  };

  const rescheduleTimeout = () => {
    if (scheduledTimeoutId) {
      clearTimeout(scheduledTimeoutId);
    }
    if (componentIsMounted.current) {
      setScheduledTimeoutId(setTimeout(() => populateTable(true), 3000));
    }
  };

  const handleRerun = (task) => {
    Utils.fetchData("loader/rerun?id=" + task.id);
  };

  const removeResults = (task) => {
    const updatedTasks = [...tasks];
    updatedTasks.splice(updatedTasks.indexOf(task), 1);
    setTasks(updatedTasks);

    Utils.fetchData(
      "loader/remove?id=" + task.id,
      false,
      null,
      false,
      false
    ).catch(function (response) {
      Utils.getErrorMessage(response, "Request failed.").then(setErrorMessage);
    });
  };

  const isTableEmpty = tasks.length === 0;

  return (
    <div className={classes.root}>
      <ErrorMessageDialog {...errorMessage} />
      <TaskDetailsDialog
        taskId={displayDetailsForTask}
        handleClosed={setDisplayDetailsForTask}
      />
      <Paper className={classes.paper}>
        {enabledFeatures.automationEnabled && (
          <>
            <FormControlLabel
              className={classes.formControlLabel}
              control={
                <CheckBox
                  label=""
                  variant="standard"
                  checked={viewManualTasks}
                  onClick={(event) => {
                    setViewManualTasks(event.target.checked)
                  }
                  }
                />
              }
              label="View Manual Tasks"/>
            <FormControlLabel
              className={classes.formControlLabel}
              control={
                <CheckBox
                  label=""
                  variant="standard"
                  checked={viewAutomatedTasks}
                  onClick={(event) => {
                    setViewAutomatedTasks(event.target.checked)
                  }
                  }
                />
              }
              label="View Automated Tasks"/>
          </>
        )
        }
        {/*<EnhancedTableToolbar title={title}/>*/}
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="small"
            aria-label="enhanced table"
          >
            {!isTableEmpty && (
              <TableHead>
                <TableRow>
                  <TableCell align="left">Task</TableCell>
                  <TableCell align="center">User</TableCell>
                  <TableCell align="center">Definition</TableCell>
                  <TableCell align="center">Updated</TableCell>
                  <TableCell align="center">Database</TableCell>
                  <TableCell align="center">Message</TableCell>
                  <TableCell align="center">Result</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {tasks.map((task) => (
                (task.automated && viewAutomatedTasks && enabledFeatures.automationEnabled) || (task.manual && viewManualTasks)) && (
                <TableRow hover role="task" tabIndex={-1}>
                  <TableCell
                    align="left"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {task.title}
                  </TableCell>
                  <TableCell
                    align="center"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {task.submitter}
                  </TableCell>
                  <TableCell
                    align="center"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {task.definition ? task.definition : "Untitled"}
                  </TableCell>
                  <Tooltip
                    title={new Date(task.lastUpdateTimestamp).toLocaleString()}
                  >
                    <TableCell
                      align="center"
                      component="th"
                      scope="row"
                      padding="default"
                    >
                      {createTimestampMessage(task.lastUpdateTimestamp)}
                    </TableCell>
                  </Tooltip>
                  <TableCell
                    align="center"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {task.database}
                  </TableCell>
                  <TableCell
                    align="center"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {task.message}
                  </TableCell>
                  <TableCell
                    align="center"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {task.completed ? (
                      <span>
                          <Button
                            variant="contained"
                            color={task.success ? "primary" : "secondary"}
                            className={classes.button}
                            onClick={() => setDisplayDetailsForTask(task.id)}
                            startIcon={
                              task.success ? <DetailsIcon/> : <ErrorIcon/>
                            }
                          >
                            Details
                          </Button>
                        {task.hasDownload && (
                          <Link download={task.filename} href={Utils.getBaseUrl() + "loader/download?id=" + task.id}>
                            <Button
                              variant="contained"
                              color={task.success ? "primary" : "secondary"}
                              className={classes.button}
                              startIcon={<DownloadIcon/>}
                            >
                              Download
                            </Button>
                          </Link>
                        )}
                        {(task.type === "EXTRACTION" || task.type === "XML_EXTRACTION") && (
                          <Button
                            variant="contained"
                            color={task.success ? "primary" : "secondary"}
                            className={classes.button}
                            onClick={() => handleRerun(task)}
                            startIcon={<ReplayIcon/>}
                          >
                            Re-Run
                          </Button>
                        )}
                        </span>
                    ) : (
                      <span>
                          <CircularProgress/>
                        </span>
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {!task.completed && (
                      <IconButton
                        onClick={() => setDisplayDetailsForTask(task.id)}
                      >
                        <InfoIcon/>
                      </IconButton>
                    )}
                    {task.manual &&
                      <IconButton onClick={() => removeResults(task)}>
                        <DeleteIcon/>
                      </IconButton>
                    }
                  </TableCell>
                </TableRow>
              ))}
              {isTableEmpty && (
                <TableRow style={{height: 53}}>
                  <TableCell>
                    <Typography align="center" color="textSecondary">
                      Empty
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Grid
          container
          spacing={1}
          style={{paddingTop: 15, paddingLeft: 15, paddingBottom: 15}}
        >
          <Grid item xs={12} container>
            <Grid item xs={1}>
              <b>Total:</b>
            </Grid>
            <Grid item xs={1}>
              Queued: {taskData.queuedTotal}
            </Grid>
            <Grid item xs={1}>
              Running: {taskData.startedTotal}
            </Grid>
            <Grid item xs={1}>
              Completed: {taskData.completedTotal}
            </Grid>
          </Grid>
          {taskData.hasBothTypes && (
            <>
              <Grid item xs={12} container>
                <Grid item xs={1}>
                  <b>Automated:</b>
                </Grid>
                <Grid item xs={1}>
                  Queued: {taskData.queuedAutomated}
                </Grid>
                <Grid item xs={1}>
                  Running: {taskData.startedAutomated}
                </Grid>
                <Grid item xs={1}>
                  Completed: {taskData.completedAutomated}
                </Grid>
              </Grid>
              <Grid item xs={12} container>
                <Grid item xs={1}>
                  <b>Manual:</b>
                </Grid>
                <Grid item xs={1}>
                  Queued: {taskData.queuedManual}
                </Grid>
                <Grid item xs={1}>
                  Running: {taskData.startedManual}
                </Grid>
                <Grid item xs={1}>
                  Completed: {taskData.completedManual}
                </Grid>
              </Grid>
            </>
          )
          }
        </Grid>
      </Paper>
    </div>
  );
}
